import { AdyenCheckout, CoreConfiguration, ICore, SubmitData } from '@adyen/adyen-web/auto'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useState } from 'react'

import bugsnagClient from '@/lib/bugsnag'
import logger from '@/lib/logger'
import { ConfirmChargeReturns, ConfirmPrepaymentReturns } from '@/stores/AppStore'
import cleanAdyenQueryParams from '@/utils/cleanAdyenQueryParams'
import errorMessageHandler, { isErrorOrResponseData } from '@/utils/errorMessageHandler'
import getLocaleWithRegion from '@/utils/getLocaleWithRegion'
import { useTranslation } from '@/utils/hooks'
import useAdyenPaymentMethods from '@/utils/hooks/useAdyenPaymentMethods'
import useSdkEvents from '@/utils/hooks/useSdkEvents'
import { isInIframe, isInSdkIframe } from '@/utils/isInIframe'
import isNetworkError from '@/utils/isNetworkError'
import StoresContext from '@/utils/StoresContext'

export interface AdyenCheckoutProps {
  amount: number
  onSubmit: (data: SubmitData) => Promise<ConfirmPrepaymentReturns<'adyen'>> | Promise<ConfirmChargeReturns>
}

export default function useAdyenCheckout({ amount, onSubmit: onSubmitProps }: AdyenCheckoutProps): ICore | null {
  const { appStore } = useContext(StoresContext)
  const { t } = useTranslation()
  const router = useRouter()
  const { sendRedirect } = useSdkEvents()
  const [checkout, setCheckout] = useState<ICore | null>(null)
  const localeWithRegion = getLocaleWithRegion(appStore.state.language)

  const paymentMethods = useAdyenPaymentMethods(amount)

  const saveDetailsLabel = t('card.ccg.submit.label', {
    value: appStore.state.quotationAmountFormatted
  })
  const otherPaymentsLabel = t('card.or_pay_with')
  const submitLabel = t('card.prepayment.submit.label')

  const onSubmit: NonNullable<CoreConfiguration['onSubmit']> = useCallback(
    async (state, dropin, actions) => {
      if (!state.isValid) return
      appStore.state.error = null

      try {
        dropin.setStatus('loading')
        const response = await onSubmitProps(state)
        const { adyenResultCode, adyenAction } = response.metaData

        actions.resolve({ resultCode: adyenResultCode, action: adyenAction || undefined })
        if (adyenResultCode === 'Authorised') {
          router.push({
            pathname: '/thank_you',
            query: cleanAdyenQueryParams(appStore.state.query)
          })
        }
      } catch (cause: unknown) {
        actions.reject()
        dropin.setStatus('ready')
        if (
          cause &&
          typeof cause === 'object' &&
          'response' in cause &&
          cause.response &&
          typeof cause.response === 'object' &&
          'data' in cause.response &&
          cause.response.data &&
          isErrorOrResponseData(cause.response.data)
        ) {
          const errorResponse = cause.response?.data
          appStore.state.error = errorMessageHandler(errorResponse)
        }
      }
    },
    [appStore.state, onSubmitProps, router]
  )

  useEffect(() => {
    if (!paymentMethods) {
      return
    }

    const initialize = async () => {
      const environment: CoreConfiguration['environment'] = appStore.state.adyenEnv === 'live' ? 'live' : 'test'
      const configuration: CoreConfiguration = {
        amount: {
          value: amount,
          currency: appStore.state.currency
        },
        showPayButton: true,
        locale: localeWithRegion,
        countryCode: appStore.state.restaurantCountry?.toUpperCase(),
        environment,
        clientKey: appStore.state.adyenPublicKey,
        analytics: {
          enabled: false
        },
        translations: {
          [localeWithRegion]: {
            'payButton.saveDetails': saveDetailsLabel,
            payButton: submitLabel,
            'paymentMethodsList.otherPayments.label': otherPaymentsLabel
          }
        },
        paymentMethodsResponse: paymentMethods,
        beforeRedirect: async (resolve, reject, data) => {
          if (isInSdkIframe(appStore)) {
            try {
              /*
               * For GET method (at least iDeal), redirect in iFrame is not working for security reasons :
               * - DOMException: Failed to read a named property 'href' from 'Location': Blocked a frame with origin "an_url" from accessing a cross-origin frame.
               * - Refused to frame 'https://ideal.ing.nl/' because an ancestor violates the following Content Security Policy directive: "frame-ancestors 'self' api.ideal.ing.nl"
               *
               * upd. After upgrading adyen-web library from v5.52.0 to v5.71.2 POST method redirection no longer works fine as well
               *
               * Solution is to redirect on the widget SDK
               */
              sendRedirect(data.method, data.url, data.data)
            } catch (err) {
              logger.error(err, 'Failed to post message to widget SDK', {
                isInIframe: isInIframe(),
                isInSdkIframe: isInSdkIframe(appStore),
                hasWindowParent: typeof window !== 'undefined' && window.parent !== window,
                origin: typeof window !== 'undefined' ? window.location.origin : 'unknown'
              })
            }
            // This reject prevents adyen redirection handling
            reject()
          } else {
            // If not in iframe continue default redirection
            resolve()
          }
        },
        onAdditionalDetails: () => {
          logger.info('onAdditionalDetails that is required so we cannot remove it')
        },
        onSubmit
      }

      try {
        const initialized = await AdyenCheckout(configuration)
        setCheckout(initialized)
      } catch (error) {
        if (!isNetworkError(error)) {
          logger.error(error)
          bugsnagClient.notify(error)
        }
      }
    }

    if (isInIframe() && !isInSdkIframe(appStore)) {
      bugsnagClient.notify(
        new Error(
          'This booking widget is in an iframe that is not the sdk iframe, this may prevent some payment methods from working properly'
        ),
        (event) => {
          event.severity = 'warning'
          event.addMetadata('widget integration', {
            windowSelf: window?.self?.location?.href,
            windowTop: window?.top?.location?.href,
            appStoreStateSdk: appStore?.state?.sdk,
            appStoreStateIsFullscreen: appStore?.state?.isFullscreen
          })
        }
      )
    }

    initialize().catch((cause: unknown) => {
      const error = new Error('failed to initialize adyen checkout', {
        cause
      })
      logger.error(error)
      bugsnagClient?.notify(error)
    })
  }, [
    amount,
    paymentMethods,
    onSubmit,
    sendRedirect,
    appStore,
    appStore.state.currency,
    appStore.state.language,
    appStore.state.query,
    appStore.state.adyenEnv,
    appStore.state.adyenPublicKey,
    saveDetailsLabel,
    otherPaymentsLabel,
    submitLabel,
    localeWithRegion
  ])

  return checkout
}
